import {
  createJwtAuthAdapter,
  createLocalTokenStorage,
  createMemoryTokenStorage,
  createMockAuthAdapter,
  type AuthAdapter,
  type TokenStorage,
} from "@crialume/core";
import type { AppConfig } from "./config";
import { MOCK_ROLE_PERMISSIONS, PERMISSIONS_CLAIM, ROLE_PERMISSIONS } from "./permissions";

/**
 * Integração com o login do backend:
 * - `sign-in` devolve o token de acesso (10 min) no corpo e o refresh token em
 *   cookie HttpOnly (`/api/auth`), que o JavaScript nunca vê;
 * - o token traz `sub` e `role` (platform ou member); nome e e-mail vêm de `GET /auth/me`.
 */
export function createAuthAdapter(config: AppConfig): AuthAdapter {
  if (config.auth.mode === "mock") {
    if (import.meta.env.PROD) {
      console.warn("[auth] modo MOCK ativo em build de produção — revise o config.json.");
    }
    return createMockAuthAdapter({
      roles: config.auth.mockRoles,
      rolePermissions: MOCK_ROLE_PERMISSIONS,
    });
  }

  const { endpoints } = config.auth;
  return createJwtAuthAdapter({
    loginPath: endpoints.signIn,
    refreshPath: endpoints.refresh,
    logoutPath: endpoints.signOut,
    profilePath: endpoints.me,
    changePasswordPath: endpoints.changePassword,
    rolesClaim: "role",
    tenantClaim: "tenant_id",
    passwordChangeClaim: "pwd_change",
    rolePermissions: ROLE_PERMISSIONS,
    permissionsClaim: PERMISSIONS_CLAIM,
  });
}

/**
 * Backend real: token só em memória (exigência do backend), compartilhado entre
 * abas por BroadcastChannel; a sessão volta pelo cookie no boot. Mock: localStorage.
 */
export function createTokenStorage(config: AppConfig): TokenStorage {
  return config.auth.mode === "mock"
    ? createLocalTokenStorage()
    : createMemoryTokenStorage({ channel: "crialume:session" });
}
